import { useMemo } from "react";

import styled, { createGlobalStyle } from "styled-components";

import { Add, AiSparkles, Upload } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, DropdownItem, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { CSV_TEMPLATE_PATH } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import type {
    CreationMenuItem,
    ICreationMenuItem,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";

const PromptCreationPopupStyles = createGlobalStyle`
    .prompt-creation-dropdown-popup.DropdownContent-container {
        z-index: 1151;
    }
`;

const StyledDropdownItem = styled(DropdownItem)`
    flex-wrap: wrap;
    width: auto;
    height: 70px;
    margin: 8px;
    padding-left: 0;
`;

interface IPromptCreationDropdown {
    isCreationEnabled: boolean;
    isLoading: boolean;
    canGeneratePrompts: boolean;
    handleManualCreation: VoidFunction;
    handleAutoGeneration: VoidFunction;
    onCSVUploadClick: VoidFunction;
}

export const PromptCreationDropdown = ({
    isCreationEnabled,
    isLoading,
    canGeneratePrompts,
    handleManualCreation,
    handleAutoGeneration,
    onCSVUploadClick,
}: IPromptCreationDropdown) => {
    const onCSVTemplateDownload = (ev: React.MouseEvent<HTMLAnchorElement>): void => {
        ev.stopPropagation();
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "add prompts/download csv template");
    };

    const items = useMemo<Record<CreationMenuItem, ICreationMenuItem>>(
        () => ({
            manual: {
                title: translate("llm.prompts.edit.prompts-table.create.option.manual.title"),
                text: translate("llm.prompts.edit.prompts-table.create.option.text"),
                icon: (
                    <Add
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: handleManualCreation,
            },
            generation: {
                title: translate("llm.prompts.edit.prompts-table.create.option.auto.title"),
                text: translate("llm.prompts.edit.prompts-table.create.option.auto.text"),
                isHidden: !canGeneratePrompts,
                icon: (
                    <AiSparkles
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: handleAutoGeneration,
            },
            csv: {
                title: translate("llm.prompts.edit.prompts-table.create.option.csv.title"),
                text: (
                    <Flex flexDirection="column">
                        <Text
                            fontVariant="listItem3"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("llm.prompts.edit.prompts-table.create.option.csv.text")}
                        </Text>
                        <a
                            onClick={onCSVTemplateDownload}
                            href={AssetsService.assetUrl(CSV_TEMPLATE_PATH)}
                            download="prompts_template.csv"
                        >
                            {translate("llm.prompts.edit.prompts-table.create.option.csv.text.link")}
                        </a>
                    </Flex>
                ),
                icon: (
                    <Upload
                        size={20}
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: onCSVUploadClick,
            },
        }),
        [handleAutoGeneration, handleManualCreation, canGeneratePrompts, onCSVUploadClick],
    );

    return (
        <>
            <PromptCreationPopupStyles />
            <Dropdown
                width="300px"
                cssClassContainer="DropdownContent-container prompt-creation-dropdown-popup"
                data-automation="prompt-creation-dropdown"
                disabled={!isCreationEnabled}
                dropdownPopupPlacement="bottom"
                appendTo="body"
                onClick={({ id }) => items?.[id as CreationMenuItem]?.onClick()}
            >
                {[
                    <IconButton
                        key="creation-dropdown-button"
                        width="auto"
                        type="outlined"
                        iconName="add"
                        iconSize="sm"
                        isDisabled={!isCreationEnabled}
                        isLoading={isLoading}
                    >
                        {translate("llm.prompts.edit.prompts-table.create.button")}
                    </IconButton>,
                    ...Object.entries(items)
                        .filter(([, { isHidden }]) => !isHidden)
                        .map(([id, { title, text, icon }]) => (
                            <StyledDropdownItem
                                key={id}
                                id={id}
                            >
                                <Flex
                                    gap="6px"
                                    padding="5px"
                                >
                                    {icon}
                                    <Flex
                                        flexDirection="column"
                                        margin="4px 0 0"
                                        gap="6px"
                                    >
                                        <Text
                                            fontVariant="input2"
                                            color={colorsPalettes.carbon[500]}
                                        >
                                            {title}
                                        </Text>
                                        <Text
                                            fontVariant="listItem3"
                                            color={colorsPalettes.carbon[300]}
                                        >
                                            {text}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </StyledDropdownItem>
                        )),
                ]}
            </Dropdown>
        </>
    );
};
